(ns quuppa.qsm.release.view
  "View for checking and upgrading to new qpe-next releases"
  (:require [linked.core :as l]
            [re-frame.core :as rf]
            [reagent.core :as r]
            ["react" :as react]
            [quuppa.components :as components]
            [quuppa.date-time.core :as date-time]
            [quuppa.peweb.tables :as tables]
            [quuppa.qsm.common.subs :as common-subs]
            [quuppa.qsm.release.events]
            [quuppa.qsm.register-qpe.utils :as register-qpe-utils]
            [quuppa.qsm.release.subs :as subs]
            [quuppa.ui.modal :as modal]
            [quuppa.ui.spinner :refer [spinner]]
            [quuppa.ui.layout :as layout]
            [quuppa.utils :as u]))

(defn download-release [opts release target-version-for-update]
  (let [{:keys [qpe-dispatch]} opts
        {:keys [version_id version channel]} release
        download-ack-pending? @(rf/subscribe [:release/download-ack-missing?])]
    [:div
     [:button.btn.btn-primary
      {:disabled download-ack-pending?
       :on-click (fn [_e]
                   (reset! target-version-for-update version)
                   (qpe-dispatch [:qsm/qpe-start-download {:version_id version_id
                                                           :version    version
                                                           :channel    channel}]))}
      "Download release"]]))

(defn install-release [qpe release opts target-version-for-update]
  (let [{current-version :version current-channel :channel} qpe
        {release-version-id :version_id release-channel :channel release-version-name :version} release
        {:keys [qpe-subscribe qpe-dispatch]} opts
        download-sub @(qpe-subscribe [:qpe/data :releases :download-status])
        {:keys [status progress] :as download-status} (or (get-in download-sub [release-channel release-version-id])
                                                          ;; backwards compatibility for QPEs who don't report channel+version
                                                          download-sub)]
    [:div
     (case status
       :started
       [:div "Downloading: 0%"]

       :downloading
       [:div "Downloading: " progress "%"]

       :another-download-in-progress
       [:div "Download already started, please wait."]

       :failed
       [:div.text-danger
        (if-let [reason (:reason download-status)]
          (str "Download failed: " reason)
          "Download failed")]

       :error
       [:div.text-danger "Download error. Technical error message: " (:message download-status)]

       :checksum-failed
       [:div.text-danger "Checksum failed. Expected " (:expected download-status) " , got " (:actual download-status) "."]

       :done
       [:div
        [:p "Download successful"]
        [:button.btn.btn-primary.mr-2
         {:on-click
          (fn [_e]
            (when (js/confirm "Are you sure?")
              (let [{:keys [channel uberjar-file-name version version_id]} download-status]
                (qpe-dispatch [:release/restart-qpe {:uberjar-file-name     uberjar-file-name
                                                     :downloaded-version    version
                                                     :downloaded-channel    channel
                                                     :downloaded-version-id version_id
                                                     :upgrade-version-id    release-version-id
                                                     :upgrade-version       release-version-name
                                                     :upgrade-channel       release-channel
                                                     :from-version          current-version
                                                     :from-channel          current-channel}]))))}
         "Restart for update"]
        [:button.btn.btn-secondary
         {:on-click (fn [_e]
                      (when (js/confirm "Discard downloaded update?")
                        (reset! target-version-for-update nil)
                        (qpe-dispatch [:release/clear-download-status])))}
         "Cancel"]]

       nil)]))

(defn update-action [_qpe _opts _release]
  (let [target-version-for-update (r/atom nil)]
    (fn [qpe opts release]
      (let [{previous-version :version previous-channel :release_channel} (:previous-installation qpe)
            {current-version :version current-channel :channel} qpe
            {release-version :version release-channel :channel} release]
        (if (and (= previous-version release-version) (= previous-channel release-channel))
          [:<> "Previously installed"]
          (if (and (= current-version release-version) (= current-channel release-channel))
            [:<> "Currently installed"]
            (if (and @target-version-for-update (= @target-version-for-update release-version))
              [install-release qpe release opts target-version-for-update]
              [download-release opts release target-version-for-update])))))))

(defn qpe-boot-update-information [{:keys [qpe-subscribe]}]
  (let [{:keys [qpe-boot-update-available?
                qpe-boot-current-version
                qpe-boot-latest-version
                installation-token
                qsm-endpoint]} @(qpe-subscribe [:qpe/data :releases :qpe-boot-update-information])
        [open? set-open] (react/useState false)]
    (when qpe-boot-current-version
      [:<>
       [:dt "qpe-boot version"]
       [:dd
        [:<>
         (if-not qpe-boot-update-available?
           qpe-boot-current-version
           [:div
            "Running " qpe-boot-current-version ", latest available is " qpe-boot-latest-version
            [:button.btn.btn-outline-primary.btn-sm.ml-3
             {:on-click #(set-open true)}
             "Update (manual)"]

            [modal/modal
             {:on-hide #(set-open false)
              :show open?}
             [modal/header
              {:close-button true}
              [modal/title "Update qpe-boot"]]
             [modal/body
              [:p
               "To update qpe-boot, you first have to stop and remove the "
               [:b "qpe"]
               " Docker container, and run a new container with a new version of the qpe-boot Docker Image"]
              [:ol
               [:li "First, stop and remove the qpe container. This will preserve data, since persistent data is stored in a Docker Volume (named qpe-data by default)."
                [:p [:code "docker stop qpe && docker rm qpe"]]]
               [:li "Then, run a new container. The command below will first pull latest version of the Docker Image and then run a new container."
                [:p [components/copy-text-input
                     ;; TODO: Store proxy host and port to QSM database so we can reconstruct the docker run command
                     ;;       Would be probably good to store the components of the whole startup command in QSM database, so that we can track how the QPE was started
                     {:value (register-qpe-utils/make-docker-run-command qsm-endpoint installation-token nil nil)}
                     "Copy"]]]]]]])]]])))

(def release-table-columns
  (l/map
    :version
    {:value-fn :version
     :sortable false
     :width "25%"
     :label "Version"}

    :created_at
    {:value-fn :created_at
     :sortable false
     :width "25%"
     :label "Created at"}

    :channel
    {:value-fn :channel
     :sortable false
     :width "25%"
     :label "Release channel"}

    :action
    {:value-fn :action
     :sortable false
     :width "25%"
     :label "Action"}))

(defn qpe-releases-table
  [{:keys [status] :as qpe} opts releases]
  [:div
   [:h4.mb-4.d-flex.flex-row.align-items-center
    "Available releases"]
   [:table.table.table-striped.table-bordered
    [tables/thead
     {:columns        release-table-columns
      :sort-key       :created_at
      :sort-order-asc true}]
    [:tbody
     (for [release releases]
       ^{:key (:created_at release)}
       [:tr
        [:td (:version release)]
        [:td (date-time/format-instant (:created_at release))]
        [:td (:channel release)]
        [:td.text-center (when (= "online" status)
                           [update-action qpe opts release])]])]]])

(defn qpe-releases-panel [{qpe-channel :channel
                           qpe-identity :identity
                           qpe-version :version
                           qpe-status :status}
                          {:keys [qpe-dispatch]}]
  (let [available-release-channels @(rf/subscribe [::common-subs/available-release-channels])
        any-channels?      (not-empty available-release-channels)
        unknown-channel?   (and any-channels? (not (contains? (set available-release-channels) qpe-channel)))]
    (react/useEffect (fn []
                       (js/console.debug "qpe version changed" qpe-identity qpe-version)
                       (qpe-dispatch [:release/qpe-version-changed qpe-version]))
                     #js [qpe-identity qpe-version])
    (react/useEffect (fn []
                       (js/console.debug "qpe status  changed" qpe-status))
                     #js [qpe-identity qpe-status])
    ;; TODO: Need to handle the scenario when restart after upgrade failed, and now QPE restarted with the previous version.
    [:div.card.bg-gray-30.p-2.pl-3.flex-shrink-1
     [:div.d-flex.align-items-center.mb-1
      [:h5.py-2.my-0.flex-grow-1 "Engine version"]]

     [:dl.m-0
      [:dt "Current version"]
      [:dd qpe-version]
      [:dt "Current channel"]
      [:dd qpe-channel
       (when unknown-channel?
         [:div.alert.alert-warning.p-2.text-gray-900.m-0
          "This channel is not longer available. Updates are from a different channel."])]]

     [:div
      [:button.btn.btn-primary
       {:on-click #(qpe-dispatch [:qpe/assoc-in [:ui :sidebar] {:type :available-releases}])
        :disabled (empty? available-release-channels)}
       "View available versions"]]]))

(defn available-releases-sidebar
  [{qpe-channel :channel
    qpe-version :version
    qpe-status :status
    :as qpe}
   {:keys [qpe-identity qpe-dispatch qpe-subscribe] :as opts}
   {:keys [show container-ref]}]
  (let [available-release-channels @(rf/subscribe [::common-subs/available-release-channels])
        any-channels?      (not-empty available-release-channels)
        unknown-channel?   (and any-channels? (not (contains? (set available-release-channels) qpe-channel)))
        offline?           (= "offline" qpe-status)
        upgrading?         (= "upgrading" qpe-status)
        [selected-channel set-selected-channel] (react/useState (if unknown-channel?
                                                                  (first available-release-channels)
                                                                  qpe-channel))
        {available-releases :data
         releases-error :error} @(qpe-subscribe [:qpe/data :releases :available-releases selected-channel])
        previous-installation @(rf/subscribe [::subs/previous-installation qpe-identity])
        upgrade            @(qpe-subscribe [:qpe/data :releases :upgrade])]

    (react/useEffect (fn []
                       (when (and qpe-identity selected-channel) ; little too defensive, but avoid request to backend with nulls
                         (rf/dispatch [:qsm/qpe-boot-check-updates qpe-identity selected-channel])))
                     ;; qpe-identity might change if the user manages to directly
                     ;; navigate from qpe a configuration page to qpe b configuration page.

                     ;; Qpe-version changes when QPE comes online after restart,
                     ;; get a new list if version did change.
                     #js [qpe-identity selected-channel qpe-version])
    (react/useEffect (fn []
                       (js/console.debug "qpe channel changed" qpe-identity qpe-channel)
                       (when-not unknown-channel?
                         (set-selected-channel qpe-channel)))
                     #js [qpe-identity qpe-channel])
    (react/useEffect (fn []
                       ;; little too defensive, but avoid request to backend with nulls. seems to happen when reloading page
                       (when (and qpe-identity selected-channel)
                         (rf/dispatch [:qsm/fetch-qpe-releases-for-channel qpe-identity selected-channel])))
                     #js [qpe-identity selected-channel])
    [layout/sidepanel
     {:show show
      :container container-ref
      :class "sidebar-lg"
      :on-hide #(qpe-dispatch [:qpe/close-sidebar])}
     [layout/sidebar-header
      {:on-close #(qpe-dispatch [:qpe/close-sidebar])
       :title "Available releases"}]

     [layout/sidebar-content
      (if (seq available-release-channels)
        [:div.form-group.col-md-6
         [:label.fw-bold
          {:for "qpe-release-channel"}
          "Select release channel"]
         [:select#qpe-release-channel.form-select
          {:value     selected-channel
           :disabled  (or offline? upgrading?)
           :on-change (fn [e]
                        (set-selected-channel (.. e -target -value))
                        (qpe-dispatch [:release/clear-download-status]))}
          (for [channel-name available-release-channels]
            [:option
             {:value channel-name
              :key   channel-name}
             channel-name])]]
        (if @(rf/subscribe [:qsm/list-qpe-release-channels-error])
          [:p.text-danger "Failed to download release channels."]
          [:p "Loading channels..."]))


      (cond
        releases-error
        [:p.text-danger
         {:title (:err-msg releases-error)}
         (str "Failed to download releases for channel '" qpe-channel "'.")]

        (nil? available-releases)
        [spinner]

        upgrading?
        [:div.pb-4
         [spinner]
         "Upgrade in progress..."]

        (and upgrade offline?)
        [:div.alert.alert-warning
         (u/str-format "QPE upgrade to version %s was started on %s, but QPE hasn't come back online after %s. Check the status of QPE manually."
                       (:upgrade-version upgrade)
                       (date-time/format-instant (:started upgrade))
                       ;; TODO get timer value from backend.
                       "5 minutes")]

        offline? [:div]

        (empty? available-releases)
        [:div
         [:p "No releases are available for the selected release channel."]]

        :else nil)

      (when (seq available-releases)
        [qpe-releases-table (assoc qpe :previous-installation previous-installation) opts available-releases])]]))
